import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, LinearProgress, Link, Snackbar, Stack, Tab, Table, TableBody, TableCell, TableHead, TablePagination,
  TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { formatDateTime } from '../utils/dateFormat';
import type {
  CachedPerformanceSummaryResponse,
  DiscoverResponse,
  DiscoveryStatus,
  ParkrunMatchesPage,
  PendingDiscoveryRow,
  PerformanceSummaryResponse,
  StravaActivitiesPage,
  StravaConnectResponse,
  StravaDisconnectResponse,
  StravaJobRow,
  StravaStatus,
  StravaSyncResponse,
} from '../api/types';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import InsightsIcon from '@mui/icons-material/Insights';
import ReactMarkdown from 'react-markdown';

type TabKey = 'activities' | 'parkruns' | 'summary';

/**
 * Single-page surface for the Strava integration. Layout, top to bottom:
 *   1. Connection panel — athlete profile, Sync now, and the latest-import
 *      watermark; hovering the watermark reveals the recent sync history.
 *   2. Tabs — Activities (paged), Parkruns (with the find-missing panel at
 *      the top), and AI Performance Summary.
 *
 * OAuth callback feedback comes through the URL: the API's /callback handler
 * 302s the browser to `/strava?connected=1` (success) or `/strava?error=<code>`
 * (failure). We pick that up via useSearchParams and surface a Snackbar
 * before clearing the query so a refresh doesn't re-show the toast.
 */
export function StravaPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<StravaStatus | null>(null);
  const [activities, setActivities] = useState<StravaActivitiesPage>({ rows: [], total: 0 });
  const [jobs, setJobs] = useState<StravaJobRow[]>([]);
  const [parkruns, setParkruns] = useState<ParkrunMatchesPage>({ rows: [], total: 0 });
  const [pendingDiscoveries, setPendingDiscoveries] = useState<PendingDiscoveryRow[]>([]);
  const [discoveryStatus, setDiscoveryStatus] = useState<DiscoveryStatus | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [parkrunsPage, setParkrunsPage] = useState(0);
  const [parkrunsPageSize, setParkrunsPageSize] = useState(10);
  const [tab, setTab] = useState<TabKey>('activities');

  const [loading, setLoading] = useState(false);
  // Tracks the sync we kick off on the OAuth callback specifically — the
  // poll-based isSyncing flag below can't see this because the StravaImportJob
  // it watches won't appear in our jobs[] state until the next poll cycle. So
  // for the OAuth window we own a dedicated flag, set true the moment we
  // POST /api/strava/sync and cleared in the finally block.
  const [initialSyncing, setInitialSyncing] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [summarising, setSummarising] = useState(false);
  const [summary, setSummary] = useState<CachedPerformanceSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  // ---- Fetch helpers ---------------------------------------------------

  const reloadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [statusResp, activitiesResp, jobsResp, parkrunResp, discoveriesResp, summaryResp, discStatusResp] = await Promise.all([
        api.get<StravaStatus>('/api/strava/status'),
        api.get<StravaActivitiesPage>(`/api/strava/activities?skip=${page * pageSize}&take=${pageSize}`),
        api.get<StravaJobRow[]>('/api/strava/jobs/recent?take=10'),
        api.get<ParkrunMatchesPage>(`/api/parkrun/matches?skip=${parkrunsPage * parkrunsPageSize}&take=${parkrunsPageSize}`),
        api.get<PendingDiscoveryRow[]>('/api/parkrun/discoveries'),
        api.get<CachedPerformanceSummaryResponse>('/api/parkrun/performance-summary'),
        api.get<DiscoveryStatus>('/api/parkrun/discovery-status'),
      ]);
      setStatus(statusResp.data);
      setActivities(activitiesResp.data);
      setJobs(jobsResp.data);
      setParkruns(parkrunResp.data);
      setPendingDiscoveries(discoveriesResp.data);
      setSummary(summaryResp.data);
      setDiscoveryStatus(discStatusResp.data);
    } catch (ex: unknown) {
      setError((ex as Error).message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, parkrunsPage, parkrunsPageSize]);

  // ---- Discovery agent actions ---------------------------------------

  const onDiscover = async () => {
    setDiscovering(true); setError(null);
    try {
      const { data } = await api.post<DiscoverResponse>('/api/parkrun/discover');
      if (data.proposalsCreated > 0) {
        setSnackbar({
          msg: `AI found ${data.proposalsCreated} potential parkrun match${data.proposalsCreated === 1 ? '' : 'es'} — review below.`,
          severity: 'success',
        });
      } else if (data.candidatesConsidered === 0) {
        setSnackbar({ msg: 'No unmatched parkrun candidates to investigate.', severity: 'info' });
      } else {
        setSnackbar({
          msg: `AI investigated ${data.candidatesConsidered} candidates but couldn’t identify any of them.`,
          severity: 'info',
        });
      }
      if (data.costCapHit) {
        setSnackbar({ msg: 'Daily AI cost cap hit — try again tomorrow.', severity: 'error' });
      }
      reloadAll();
    } catch (ex: unknown) {
      setError((ex as Error).message);
    } finally {
      setDiscovering(false);
    }
  };

  const onApproveDiscovery = async (id: string) => {
    try {
      await api.post(`/api/parkrun/discoveries/${id}/approve`);
      setSnackbar({ msg: 'Match confirmed — added to your parkrun history.', severity: 'success' });
      reloadAll();
    } catch (ex: unknown) {
      setError((ex as Error).message);
    }
  };

  const onRejectDiscovery = async (id: string) => {
    try {
      await api.post(`/api/parkrun/discoveries/${id}/reject`);
      setSnackbar({ msg: 'Match rejected.', severity: 'info' });
      reloadAll();
    } catch (ex: unknown) {
      setError((ex as Error).message);
    }
  };

  // ---- Performance summary actions (PBI 49) --------------------------

  const onGenerateSummary = async () => {
    setSummarising(true); setError(null);
    try {
      const { data } = await api.post<PerformanceSummaryResponse>('/api/parkrun/performance-summary');
      // Refresh the cached view so the freshness flag flips to up-to-date
      // immediately, hiding the regenerate button without another round-trip.
      const cached = await api.get<CachedPerformanceSummaryResponse>('/api/parkrun/performance-summary');
      setSummary(cached.data);
      if (data.notEnoughData) {
        setSnackbar({ msg: 'Not enough matched parkrun data for a summary yet.', severity: 'info' });
      }
    } catch (ex: unknown) {
      setError((ex as Error).message);
    } finally {
      setSummarising(false);
    }
  };

  // The most recent job tells us whether a sync is in flight. Jobs come back
  // newest-first, so jobs[0] is current state. We treat Queued the same as
  // Running for UI purposes — both mean "user should wait".
  const inflightJob = jobs[0]?.status === 'Running' || jobs[0]?.status === 'Queued'
    ? jobs[0]
    : null;
  const isSyncing = !!inflightJob;

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // ---- Poll while a sync job is in flight ------------------------------
  // jobs[0].status is Running or Queued => poll the jobs endpoint every 3s
  // until it transitions to Completed / Failed. When that transition lands,
  // do a full reloadAll so the new activities + matches + summary all
  // refresh together.
  useEffect(() => {
    if (!isSyncing) return;
    const handle = setInterval(async () => {
      try {
        const { data } = await api.get<StravaJobRow[]>('/api/strava/jobs/recent?take=10');
        setJobs(data);
        const top = data[0];
        if (top && top.status !== 'Running' && top.status !== 'Queued') {
          // Sync just finished — pull the rest of the data once.
          reloadAll();
        }
      } catch {
        // Swallow polling errors; UI will recover on the next interval or
        // the next user-initiated reload.
      }
    }, 3000);
    return () => clearInterval(handle);
  }, [isSyncing, reloadAll]);

  // ---- OAuth callback feedback ----------------------------------------

  useEffect(() => {
    const connected = searchParams.get('connected');
    const err = searchParams.get('error');
    if (connected === '1') {
      setSnackbar({ msg: 'Strava connected. Importing your activities…', severity: 'success' });
      setSearchParams({}, { replace: true });
      // Kick off a sync immediately — without this the user waits up to
      // 15 min for the next StravaIncrementalSyncHostedService tick. The
      // polling effect above will surface the progress bar.
      setInitialSyncing(true);
      (async () => {
        try {
          await api.post<StravaSyncResponse>('/api/strava/sync');
          await reloadAll();
        } catch (ex) {
          setError((ex as Error).message);
        } finally {
          setInitialSyncing(false);
        }
      })();
    } else if (err) {
      setSnackbar({ msg: `Strava connection failed: ${err}`, severity: 'error' });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, reloadAll]);

  // ---- Actions ---------------------------------------------------------

  const onConnect = async () => {
    try {
      const { data } = await api.get<StravaConnectResponse>('/api/strava/connect');
      // Same-tab navigation so the callback returns the user to the SPA via
      // /strava?connected=1 rather than landing on a closed popup.
      window.location.href = data.authorizeUrl;
    } catch (ex: unknown) {
      setError((ex as Error).message);
    }
  };

  const onDisconnect = async () => {
    setConfirmDisconnect(false);
    try {
      const { data } = await api.delete<StravaDisconnectResponse>('/api/strava/connection');
      setSnackbar({
        msg: data.wasConnected
          ? 'Disconnected. Your activity history is preserved — reconnect to resume sync.'
          : 'Already disconnected.',
        severity: 'info',
      });
      reloadAll();
    } catch (ex: unknown) {
      setError((ex as Error).message);
    }
  };

  // ---- Derived view models --------------------------------------------

  const athleteName = useMemo(() => {
    if (!status?.isConnected) return '';
    return [status.firstName, status.lastName].filter(Boolean).join(' ') || `Athlete #${status.athleteId}`;
  }, [status]);

  // Prefer the most recent completed sync job over the activity-watermark on
  // the connection row — the watermark is the latest activity's start time
  // (used for incremental sync), not when the import actually ran.
  const lastSyncAtUtc = useMemo(() => {
    const lastCompleted = jobs.find((j) => j.completedAtUtc);
    return lastCompleted?.completedAtUtc ?? status?.lastImportedAtUtc ?? null;
  }, [jobs, status]);

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Strava sync in flight — page-level banner so it stays visible across
          all tabs while the polling effect waits for the job to finish.
          initialSyncing covers the OAuth-callback window where jobs[] is
          still stale and the poll-based isSyncing can't see the in-flight job. */}
      {(isSyncing || initialSyncing) && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ alignItems: 'center' }}>
          <Box>
            <strong>Importing your Strava activities…</strong>
            {inflightJob?.importedCount && inflightJob.importedCount > 0 && (
              <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                {inflightJob.importedCount} so far
              </Typography>
            )}
          </Box>
          <LinearProgress sx={{ mt: 1 }} />
        </Alert>
      )}

      {/* ---- Connection panel ---------------------------------------- */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Strava connection</Typography>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={reloadAll} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {loading && !status && <CircularProgress size={20} />}

          {status && !status.isConnected && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography color="text.secondary" sx={{ flexGrow: 1 }}>
                Not connected. Link your Strava account to import activities.
              </Typography>
              <Button variant="contained" startIcon={<LinkIcon />} onClick={onConnect}>
                Connect Strava
              </Button>
            </Stack>
          )}

          {status?.isConnected && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={status.profileImageUrl ?? undefined}>{athleteName[0] ?? 'A'}</Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1">{athleteName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Athlete #{status.athleteId} · {status.scope ?? '(no scope)'}
                  {lastSyncAtUtc && <> · Last imported {formatDateTime(lastSyncAtUtc)}</>}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={() => setConfirmDisconnect(true)}
              >
                Disconnect
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ---- Tabs (Activities / Parkruns / AI Summary) -------------- */}
      {status?.isConnected && (
        <Card>
          <Tabs
            value={tab}
            onChange={(_, v: TabKey) => setTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              value="activities"
              label={activities.total > 0 ? `Activities (${activities.total})` : 'Activities'}
            />
            <Tab
              value="parkruns"
              label={parkruns.total > 0 ? `Parkruns (${parkruns.total})` : 'Parkruns'}
              icon={<DirectionsRunIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              value="summary"
              label="AI Performance Summary"
              icon={<InsightsIcon fontSize="small" />}
              iconPosition="start"
              disabled={parkruns.total === 0}
            />
          </Tabs>

          {/* ---- Activities tab ------------------------------------- */}
          {tab === 'activities' && (
            <CardContent>
              {activities.rows.length === 0
                ? (
                  // Only show the genuine empty state once we KNOW sync is
                  // done — otherwise it reads as a bug ("connected but no
                  // data?") during the seconds it takes the initial sync to
                  // run. While anything's in flight, defer to the top banner.
                  isSyncing || initialSyncing || loading
                    ? (
                      <Typography color="text.secondary">
                        Importing your activities — this can take a minute on the first sync.
                      </Typography>
                    )
                    : (
                      <Typography color="text.secondary">
                        No activities found in your Strava account.
                      </Typography>
                    )
                )
                : (
                  <>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Sport</TableCell>
                          <TableCell align="right">Distance</TableCell>
                          <TableCell align="right">Moving</TableCell>
                          <TableCell align="right">Pace</TableCell>
                          <TableCell align="right">HR avg</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activities.rows.map((a) => (
                          <TableRow key={a.id} hover>
                            <TableCell>{formatDateTime(a.startDateLocal)}</TableCell>
                            <TableCell>{a.name}</TableCell>
                            <TableCell><Chip label={a.sportType} size="small" /></TableCell>
                            <TableCell align="right">{formatKm(a.distanceMeters)}</TableCell>
                            <TableCell align="right">{formatDuration(a.movingTimeSeconds)}</TableCell>
                            <TableCell align="right">{formatPace(a.distanceMeters, a.movingTimeSeconds)}</TableCell>
                            <TableCell align="right">
                              {a.averageHeartrate ? `${Math.round(a.averageHeartrate)}` : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={activities.total}
                      page={page}
                      onPageChange={(_, p) => setPage(p)}
                      rowsPerPage={pageSize}
                      onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  </>
                )}
            </CardContent>
          )}

          {/* ---- Parkruns tab (Find-missing on top, then matches) -- */}
          {tab === 'parkruns' && (
            <CardContent>
              {/* Find missing parkruns with AI — HIDDEN BY DEFAULT.
                  Each click burns up to MaxTokensPerRun (50k) of LLM tokens
                  per user, which doesn't scale for accounts with thousands of
                  activities. We keep the markup in the DOM so it can be
                  re-enabled for demos via CSS without redeploying:
                      .ai-discovery-section { display: block !important; }
                  Inline style takes precedence so it stays off by default. */}
              {((discoveryStatus?.availableForAgent ?? 0) > 0 || pendingDiscoveries.length > 0) && (
                <Box
                  className="ai-discovery-section"
                  style={{ display: 'none' }}
                  sx={{ pb: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <PsychologyIcon color="secondary" />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      Find missing parkruns with AI
                    </Typography>
                    {(discoveryStatus?.availableForAgent ?? 0) > 0 && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={discovering ? <CircularProgress size={16} /> : <PsychologyIcon />}
                        onClick={onDiscover}
                        disabled={discovering}
                      >
                        {discovering
                          ? 'Searching…'
                          : `Find missing parkruns (${discoveryStatus?.availableForAgent} to check)`}
                      </Button>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    The agent looks at activities that match the parkrun shape (5km, Saturday morning, GPS)
                    but didn’t auto-match a venue. It searches the venue database, considers nearby
                    parkruns by GPS, and proposes matches for your confirmation.
                  </Typography>

                  {pendingDiscoveries.length > 0 && (
                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Activity</TableCell>
                          <TableCell>Proposed venue</TableCell>
                          <TableCell>AI reasoning</TableCell>
                          <TableCell align="right">Decide</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingDiscoveries.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                              #{d.stravaActivityId}
                            </TableCell>
                            <TableCell>
                              <Chip label={d.proposedVenueName} size="small" color="secondary" />
                              {d.proposedVenueCity && (
                                <Typography component="span" color="text.secondary" sx={{ ml: 1, fontSize: 12 }}>
                                  {d.proposedVenueCity}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                              {d.agentReasoning}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Confirm match">
                                <IconButton color="success" size="small" onClick={() => onApproveDiscovery(d.id)}>
                                  <ThumbUpIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton color="error" size="small" onClick={() => onRejectDiscovery(d.id)}>
                                  <ThumbDownIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}

              {/* Matched parkruns table. */}
              {parkruns.rows.length === 0
                ? (
                  <Typography color="text.secondary">
                    No parkruns matched yet — visit Hove Promenade, Preston Park, or any other
                    Sussex parkrun on a Saturday at 9am, sync Strava, and they will appear here.
                  </Typography>
                )
                : (
                  <>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Venue</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell align="right">Strava activity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parkruns.rows.map((p) => (
                          <TableRow key={`${p.stravaActivityId}-${p.parkrunEventId}`} hover>
                            <TableCell>{formatDateTime(p.activityStartLocal)}</TableCell>
                            <TableCell>
                              <Chip label={p.venueName} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell>{p.city}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                              <Link
                                href={`https://www.strava.com/activities/${p.stravaActivityId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                              >
                                #{p.stravaActivityId}
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={parkruns.total}
                      page={parkrunsPage}
                      onPageChange={(_, p) => setParkrunsPage(p)}
                      rowsPerPage={parkrunsPageSize}
                      onRowsPerPageChange={(e) => { setParkrunsPageSize(parseInt(e.target.value, 10)); setParkrunsPage(0); }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  </>
                )}
            </CardContent>
          )}

          {/* ---- AI Performance Summary tab ------------------------- */}
          {tab === 'summary' && (
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  AI-written analysis grounded in your matched parkruns and the historical weather at each
                  start point. Time-windowed view (12 / 6 / 3 months + 3-month outlook) followed by
                  weather correlation.
                  {summary?.generatedAtUtc && (
                    <> Last generated {formatDateTime(summary.generatedAtUtc)} from {summary.cachedMatchCount} parkruns.</>
                  )}
                </Typography>
                {/* Hide the button entirely when nothing has changed since the
                    cached summary — there's nothing useful for it to do. Show
                    "Generate summary" first time, "Regenerate" when N more
                    parkruns have arrived. */}
                {(!summary?.markdown || summary.isStale) && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={summarising ? <CircularProgress size={16} /> : <InsightsIcon />}
                    onClick={onGenerateSummary}
                    disabled={summarising}
                  >
                    {summarising
                      ? 'Generating…'
                      : summary?.markdown
                        ? `Regenerate (+${Math.max(0, summary.currentMatchCount - summary.cachedMatchCount)} new)`
                        : 'Generate summary'}
                  </Button>
                )}
              </Stack>
              {summary?.markdown
                ? (
                  <Box sx={{
                    mt: 2, px: 2, py: 1, borderRadius: 1,
                    bgcolor: 'background.default',
                    '& h2': { fontSize: '1.15rem', mt: 2, mb: 1 },
                    '& h3': { fontSize: '1.0rem',  mt: 1.5, mb: 0.5 },
                    '& ul': { pl: 3, my: 0.5 },
                    '& p':  { my: 0.5 },
                  }}>
                    <ReactMarkdown>{summary.markdown}</ReactMarkdown>
                  </Box>
                )
                : (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    Click &quot;Generate summary&quot; to get an AI-written read on your performance trends and
                    weather sensitivity.
                  </Typography>
                )}
            </CardContent>
          )}
        </Card>
      )}

      {/* ---- Confirm disconnect dialog ------------------------------- */}
      <Dialog open={confirmDisconnect} onClose={() => setConfirmDisconnect(false)}>
        <DialogTitle>Disconnect Strava?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your activity history will be preserved. You can reconnect at any time
            and sync will resume from where it left off.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisconnect(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onDisconnect}>Disconnect</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar
          ? <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>{snackbar.msg}</Alert>
          : undefined}
      </Snackbar>
    </Stack>
  );
}

// ---- Formatting helpers ----------------------------------------------

function formatKm(meters: number): string {
  return `${(meters / 1000).toFixed(2)} km`;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatPace(distanceMeters: number, movingTimeSeconds: number): string {
  if (distanceMeters < 100 || movingTimeSeconds < 1) return '—';
  const minPerKm = (movingTimeSeconds / 60) / (distanceMeters / 1000);
  const mins = Math.floor(minPerKm);
  const secs = Math.round((minPerKm - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, '0')}/km`;
}

